/**
 * Shell Utility Functions (Module Federation Edition)
 * --------------------------------------------------
 * - Replaces all SystemJS logic with Module Federation remote loader
 * - Supports Vue 2 + Vue 3 MFEs
 * - Does NOT rely on shared Vue instances
 *
 * @author  Harvinder Singh <harvinder@maropost.com>
 * @created 2025-09-12
 */

import { useUserStore } from "../stores/userStore";
import type { LifeCycles, LifeCycleFn } from "single-spa";
import { shellReady } from "@/lib/config/spa/shell-ready";
import { canAccessRoute } from "../router/middleware/route-access";
export const accessMapCache: Record<string, any[]> = {}

/* ============================================================================
   DOM ELEMENT GETTER
   ============================================================================
   Creates and resolves a DOM element where the MFE will mount.
   Waits until the shell outlet exists before proceeding.
============================================================================ */

/**
 * Returns a function that resolves a DOM element for a given MFE.
 */
export function createDomElementGetter(
  appName: string
): () => Promise<HTMLElement> {
  return (): Promise<HTMLElement> =>
    new Promise<HTMLElement>((resolve) => {
      const getElement = (): void => {
        const outlet = document.getElementById("mfe-parent");

        // Retry until shell outlet is available
        if (!outlet) {
          setTimeout(getElement, 10);
          return;
        }

        // Clear previous MFE
        outlet.innerHTML = "";

        // Create new MFE mount node
        const mfeDiv = document.createElement("div");
        mfeDiv.id = `mfe-outlet-${appName}`;
        outlet.appendChild(mfeDiv);

        resolve(mfeDiv);
      };

      getElement();
    });
}

/* ============================================================================
   ROUTE MATCHER
   ============================================================================
   Utility to check whether current location matches a product route.
============================================================================ */

/**
 * Checks if the current URL matches the product route.
 */
export function matchProductRoute(
  location: Location,
  routeStartsWith: string
): boolean {
  return (
    location.pathname === `/${routeStartsWith}` ||
    location.pathname.startsWith(`/${routeStartsWith}/`)
  );
}

/* ============================================================================
   ACCESS CHECK
   ============================================================================
   Ensures user has access to the product before mounting the MFE.
   Redirects to preview page if access is missing.
============================================================================ */

/**
 * Validates product access before allowing mount.
 */
async function ensureProductAccess(
  productName: string | string[],
  routeStartsWith: string
): Promise<boolean> {
  // Ensure shell initialization is complete
  await shellReady;

  const userStore = useUserStore();

  const isRouteMatch =
    window.location.pathname === `/${routeStartsWith}` ||
    window.location.pathname.startsWith(`/${routeStartsWith}/`);

  if (!isRouteMatch) return false;

  const products = Array.isArray(productName)
    ? productName
    : [productName];

  // User has access if they have access to ANY of the products
  const hasAccess = products.some(
    (product) =>
      userStore.getProductAccess(product) &&
      userStore.getProductAccessOnRoles(product)
  );

  if (!hasAccess) {
    // For multiple products, use the first product for preview
    // You can change this logic if a specific product should be preferred.
    const previewProduct = products[0];
    const previewPath = `/preview/${previewProduct}`;

    // Soft navigation without reload
    window.history.pushState({}, "", previewPath);
    window.dispatchEvent(new PopStateEvent("popstate"));

    return false;
  }

  return true;
}

/* ============================================================================
   ACCESS GUARD WRAPPER
   ============================================================================
   Wraps a Single-SPA lifecycle with an access check.
============================================================================ */

/**
 * Wraps an MFE loader with an access guard.
 */
export function withAccessGuard(
  appLoader: () => Promise<LifeCycles<Record<string, unknown>>>,
  productName: string | string[],
  routeStartsWith: string
): () => Promise<LifeCycles<Record<string, unknown>>> {
  return async (): Promise<LifeCycles<Record<string, unknown>>> => {
    const app = await appLoader();

    const originalMount = app.mount;

    // Normalize mount lifecycle into array (UNCHANGED)
    const mounts: LifeCycleFn<Record<string, unknown>>[] =
      Array.isArray(originalMount)
        ? originalMount
        : originalMount
        ? [originalMount]
        : [];

    return {
      ...app,

      async mount(props) {
        /**
         * ------------------------------------------------------------
         * MF COMPONENT RENDER BYPASS (NEW — SAFE)
         * ------------------------------------------------------------
         * This is ONLY set by the shell right before calling moduleEntry.
         * It does NOT affect normal navigation.
         */
        const isMfComponentRender =
          typeof window !== "undefined" &&
          window.history?.state?.__mfComponentRender === true;

        if (!isMfComponentRender) {
          const hasAccess = await ensureProductAccess(
            productName,
            routeStartsWith
          );

          if (!hasAccess) return Promise.resolve();
        }

        // ORIGINAL MOUNT FLOW (UNCHANGED)
        for (const mountFn of mounts) {
          await mountFn(props);
        }
      },
    };
  };
}


/* ============================================================================
   MODULE FEDERATION RUNTIME HELPERS
   ============================================================================
   Responsible for loading and initializing remote containers safely.
============================================================================ */

declare const __webpack_share_scopes__: {
  default: unknown;
};

declare global {
  interface Window {
    __webpack_share_scopes__?: {
      default?: unknown;
    };
  }
}

/**
 * Remotes built as ES modules (e.g. Vite + @originjs/vite-plugin-federation).
 * Their remoteEntry.js is an ESM that exports get/init and does NOT register a
 * window global, so it must be loaded via dynamic import() instead of a classic
 * <script>. Webpack MF remotes are not listed here and keep their original path.
 */
const ESM_REMOTES = new Set<string>(["merchandise"]);

/**
 * Cache to avoid reloading the same remote container.
 */
const containerCache: Map<string, WebpackContainer> = new Map();

/**
 * Minimal Webpack Module Federation container interface.
 */
interface WebpackContainer {
  init(shareScope: unknown): Promise<void>;
  get(module: string): Promise<() => LifeCycles<Record<string, unknown>>>;
}

/**
 * Host share scope used by Module Federation.
 * Prefer window scope so onboarding (`register-remote.js`) and single-spa
 * initialize remotes with the same object reference.
 */
function getHostShareScope(): unknown {
  return (
    window.__webpack_share_scopes__?.default ?? __webpack_share_scopes__.default
  );
}

/**
 * Loads a remoteEntry.js file as a classic script.
 */
function loadRemoteScript(url: string): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");

    script.src = url;
    script.type = "text/javascript";
    script.async = false; // IMPORTANT: Required for MF execution order
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error(`[MF Loader] Failed to load ${url}`));

    document.head.appendChild(script);
  });
}

/**
 * Initialize a remote container once. Ignore double-init / share-scope conflicts
 * when onboarding (or another loader) already initialized the same remote.
 */
async function ensureContainerInitialized(
  container: WebpackContainer
): Promise<void> {
  const shareScope = getHostShareScope();
  if (shareScope == null) return;

  try {
    await container.init(shareScope);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    // Remote was already initialized (often by onboarding preload on Marketing).
    if (
      message.includes("already been initialized") ||
      message.includes("share scope")
    ) {
      return;
    }
    throw error;
  }
}

/**
 * Loads and initializes a remote container.
 */
async function loadRemoteContainer(
  remoteBaseUrl: string,
  remoteName: string
): Promise<WebpackContainer> {
  if (containerCache.has(remoteName)) {
    return containerCache.get(remoteName)!;
  }

  const win = window as unknown as Record<string, WebpackContainer | undefined>;
  let container = win[remoteName];

  // Reuse container already injected by onboarding / register-remote.
  if (!container) {
    const remoteEntryUrl = `${remoteBaseUrl.replace(
      /\/$/,
      ""
    )}/remoteEntry.js?v=${Date.now()}`;

    if (ESM_REMOTES.has(remoteName)) {
      // ESM (Vite) remotes: remoteEntry is an ES module that exports get/init —
      // no window global, so it must be loaded via dynamic import().
      container = (await import(
        /* webpackIgnore: true */ remoteEntryUrl
      )) as WebpackContainer;
    } else {
      // Webpack MF remotes
      await loadRemoteScript(remoteEntryUrl);
      container = win[remoteName];
    }
  }

  if (!container) {
    throw new Error(
      `[MF Loader] Container '${remoteName}' not found on window`
    );
  }

  // Onboarding may have already initialized this remote (e.g. Marketing → Commerce).
  // Re-init with a different share-scope object throws; ignore that and reuse.
  await ensureContainerInitialized(container);

  containerCache.set(remoteName, container);
  return container;
}

/* ============================================================================
   REMOTE LOADER WITH FALLBACK
   ============================================================================
   Loads a remote MFE safely and falls back to an unavailable route on failure.
============================================================================ */

/**
 * Loads an MFE with graceful failure handling.
 */
export function loadWithFallback(
  remoteBaseUrl: string,
  remoteName: string,
  productKey: string
): () => Promise<LifeCycles<Record<string, unknown>>> {
  return async (): Promise<LifeCycles<Record<string, unknown>>> => {
    try {
      // Notify shell: loading has started
      window.dispatchEvent(
        new CustomEvent("MFE_LOADED", {
          detail: true
        })
      );
      const container = await loadRemoteContainer(
        remoteBaseUrl,
        remoteName
      );
      await ensureAccessMap(remoteName, container);
      const factory = await container.get("./Mount");
      return factory();
    } catch (error) {
      // Notify shell: loading failed / stop loader
      window.dispatchEvent(
        new CustomEvent("MFE_LOADED", {
          detail: false
        })
      );
      console.error(
        `[MFE Error] Failed to load ${productKey}`,
        error
      );

      // Redirect to unavailable page
      const unavailablePath = `/unavailable/${encodeURIComponent(
        productKey
      )}`;

      window.history.pushState({}, "", unavailablePath);
      window.dispatchEvent(new PopStateEvent("popstate"));

      // No-op lifecycle to keep single-spa stable
      return {
        bootstrap: async () => undefined,
        mount: async () => undefined,
        unmount: async () => undefined,
      };
    }
  };
}

/**
 * List of modules that include accountId in the URL structure.
 * Example paths:
 * /accounts/:accountId/...
 * /commerce/:accountId/...
 * /service/:accountId/...
 */
const ACCOUNT_MODULE_REGEX = /^\/(accounts|commerce|service)\/\d+/;

/**
 * Replaces the accountId in a given path if the path follows the pattern:
 *
 *  /accounts/:accountId/...
 *  /commerce/:accountId/...
 *  /service/:accountId/...
 *
 * Example:
 *  Input Path:  /commerce/25435/products
 *  New ID:      99999
 *
 *  Output:      /commerce/99999/products
 *
 * If the path does not match the expected structure, the original path is returned unchanged.
 *
 * @param path - Current route path (e.g., "/commerce/25435/products")
 * @param newAccountId - The accountId that should replace the existing one
 *
 * @returns Updated path with the new accountId
 */
export function getAccountSwitchPath(
  path: string,
  newAccountId: string | number
): string {
  if (!path) return path;

  return path.replace(
    ACCOUNT_MODULE_REGEX,
    `/$1/${newAccountId}`
  );
}


/**
 * Validates whether the current route belongs to the active account.
 *
 * Rules:
 * 1. If the path is root "/" (segments length = 0)
 *    → return true only if activeAccountId exists
 *
 * 2. If path contains less than 3 segments
 *    Example:
 *      /accounts/20001
 *      /service/20001
 *    → return false because a page after accountId is required
 *
 * 3. If path contains at least 3 segments
 *    Example:
 *      /accounts/20001/dashboard
 *      /service/20001/tickets
 *      /commerce/20001/products
 *
 *    → validate:
 *        - module is allowed
 *        - accountId matches activeAccountId
 *
 * @param path - current route path
 * @param activeAccountId - accountId currently stored in the application state
 *
 * @returns boolean
 *  true  → path belongs to active account
 *  false → invalid structure or account mismatch
 */
export function isActiveAccountPath(
  path: string,
  activeAccountId: string | null
): boolean {

  if (!path) return false;

  // Split path and remove empty segments
  const segments = path.split("/").filter(Boolean);

  /**
   * CASE 1
   * Root path "/"
   */
  if (segments.length === 0) {
    return !!activeAccountId;
  }

  /**
   * CASE 2
   * Invalid structure (must have at least 3 segments)
   *
   * Example invalid:
   * /accounts/20001
   * /service/20001
   */
  if (segments.length > 1 && segments.length < 3) {
    return false;
  }

  /**
   * CASE 3
   * Valid structure expected:
   * /module/accountId/page
   */
  const [module, accountId] = segments;

  const allowedModules = ["accounts", "service", "commerce"];

  if (!allowedModules.includes(module)) {
    return false;
  }

  if (!accountId || !activeAccountId) {
    return false;
  }

  return accountId === activeAccountId;
}

async function ensureAccessMap(appName: string, container: any) {
  if (accessMapCache[appName]) return accessMapCache[appName]

  const factory = await container.get('./RouteAccessMap')
  const module = factory()

  const map = module.getAccountsAccessMap()
  accessMapCache[appName] = map
  const userStore = useUserStore();
  canAccessRoute(window.location.pathname, userStore)
}